import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { UsersFour, Trophy, TrendUp, UserCircle, ClipboardText, Warning, CalendarBlank } from "@phosphor-icons/react";

/** Phase 6C — Role-based dashboard widgets.
 *  Reads from /api/dashboards/{executive|team-leader|hr}. Renders per user role.
 */
const Stat = ({ label, value, hint, tone = "sky", testid, icon: Icon }) => (
  <div className="stat-card" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-3xl font-semibold mt-2 text-slate-900">{value ?? "—"}</div>
        {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
      </div>
      {Icon && (
        <div className={`w-10 h-10 rounded-md grid place-items-center bg-${tone}-50 text-${tone}-600`}>
          <Icon size={20} />
        </div>
      )}
    </div>
  </div>
);

function ExecutiveWidgets({ d }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-executive">
      <Stat label="Assigned" value={d.assigned} icon={UserCircle} tone="sky" testid="exec-stat-assigned" />
      <Stat label="Pending" value={d.pending} icon={ClipboardText} tone="amber" testid="exec-stat-pending" />
      <Stat label="Completed" value={d.completed} icon={Trophy} tone="emerald" testid="exec-stat-completed" />
      <Stat label="Rejected" value={d.rejected} icon={Warning} tone="rose" testid="exec-stat-rejected" />
      <Stat label="Today · Assignments" value={d.today_assignments} icon={CalendarBlank} tone="violet" testid="exec-stat-today-assignments" />
      <Stat label="Today · Follow-ups" value={d.today_followups} icon={CalendarBlank} tone="sky" testid="exec-stat-today-followups" />
      <Stat label="Capacity" value={`${d.capacity?.current ?? 0}/${d.capacity?.max ?? 0}`}
            hint={`${d.capacity?.available ?? 0} available`} icon={UsersFour} tone="emerald" testid="exec-stat-capacity" />
      <Stat label="Performance" value={`${d.performance_pct ?? 0}%`} icon={TrendUp} tone="amber" testid="exec-stat-performance" />
    </div>
  );
}

function TeamLeaderWidgets({ d }) {
  return (
    <div className="space-y-4" data-testid="dashboard-team-leader">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Executives" value={d.executives} icon={UsersFour} testid="tl-stat-executives" />
        <Stat label="Assignments today" value={d.assignments_today} icon={CalendarBlank} tone="violet" testid="tl-stat-assign-today" />
        <Stat label="Pending" value={d.pending} icon={ClipboardText} tone="amber" testid="tl-stat-pending" />
        <Stat label="Completed" value={d.completed} icon={Trophy} tone="emerald" testid="tl-stat-completed" />
        <Stat label="Rejected" value={d.rejected} icon={Warning} tone="rose" testid="tl-stat-rejected" />
        <Stat label="Success rate" value={`${d.success_pct ?? 0}%`} icon={TrendUp} tone="sky" testid="tl-stat-success" />
        <Stat label="Average capacity" value={d.average_capacity} icon={UsersFour} tone="emerald" testid="tl-stat-avg-capacity" />
      </div>

      {d.workload_distribution?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="tl-workload-distribution">
          <div className="text-sm font-semibold mb-3">Workload distribution</div>
          <div className="space-y-2">
            {d.workload_distribution.map((w) => (
              <div key={w.employee_id} className="flex items-center gap-3 text-sm" data-testid={`tl-workload-${w.employee_id}`}>
                <div className="w-40 truncate">
                  <div className="font-medium">{w.name}</div>
                  <div className="text-[10px] text-slate-400">{w.code}</div>
                </div>
                <div className="flex-1 bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div className={`h-full ${w.utilization_pct >= 100 ? "bg-red-500" : w.utilization_pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                       style={{ width: `${Math.min(100, w.utilization_pct)}%` }} />
                </div>
                <div className="w-24 text-right text-xs text-slate-500 tabular-nums">
                  {w.load}/{w.max} · {w.utilization_pct}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HRWidgets({ d }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-hr">
      <Stat label="Pending HR approval" value={d.pending_approval} icon={ClipboardText} tone="amber" testid="hr-stat-pending" />
      <Stat label="Returned" value={d.returned} icon={Warning} tone="rose" testid="hr-stat-returned" />
      <Stat label="Approved" value={d.approved} icon={Trophy} tone="emerald" testid="hr-stat-approved" />
      <Stat label="Rejected" value={d.rejected} icon={Warning} tone="rose" testid="hr-stat-rejected" />
      <Stat label="Today activities" value={d.today_activities} icon={CalendarBlank} tone="sky" testid="hr-stat-today" />
      <Stat label="Overall performance" value={`${d.overall_performance_pct ?? 0}%`} icon={TrendUp} tone="violet" testid="hr-stat-performance" />
    </div>
  );
}

export default function RoleDashboardWidgets() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setErr(null);
    let endpoint = null;
    if (["sde", "executive", "senior_executive", "sdl"].includes(user.role)) endpoint = "/dashboards/executive";
    else if (["team_lead"].includes(user.role)) endpoint = "/dashboards/team-leader";
    else if (["hr"].includes(user.role)) endpoint = "/dashboards/hr";
    else if (user.role === "admin") endpoint = "/dashboards/team-leader";   // admin sees TL view
    if (!endpoint) { setLoading(false); return; }
    try {
      const r = await api.get(endpoint);
      setData({ endpoint, ...r.data });
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load dashboard");
    } finally { setLoading(false); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-slate-400" data-testid="role-dashboard-loading">Loading…</div>;
  if (err) return <div className="text-sm text-red-600" data-testid="role-dashboard-error">{err}</div>;
  if (!data) return null;

  return (
    <section data-testid="role-dashboard-widgets">
      <h3 className="text-lg font-semibold mb-3">
        {data.endpoint === "/dashboards/executive" ? "My workspace" :
         data.endpoint === "/dashboards/team-leader" ? "Team overview" :
         "HR overview"}
      </h3>
      {data.endpoint === "/dashboards/executive" && <ExecutiveWidgets d={data} />}
      {data.endpoint === "/dashboards/team-leader" && <TeamLeaderWidgets d={data} />}
      {data.endpoint === "/dashboards/hr" && <HRWidgets d={data} />}
    </section>
  );
}
